/**
 * @file apps/web/src/components/AuditTrail.tsx
 * @description Chronological audit trail timeline for approval workflows.
 * Renders immutable audit log events with icons, actor info, and payload preview.
 */

import { useState } from 'react';
import {
  CheckCircle, XCircle, PlayCircle, SkipForward, UserCheck,
  AlertCircle, Clock, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Badge } from './Badge.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuditEvent {
  id:           string;
  event_type:   string;
  created_at:   string;
  actor_id:     string | null;
  actor_email:  string | null;
  actor_name:   string | null;
  step_id:      string | null;
  payload:      Record<string, unknown> | null;
}

interface AuditTrailProps {
  events:    AuditEvent[];
  loading?:  boolean;
  className?: string;
  // Client-facing view: never show the raw event payload (internal field
  // names and values are for the delivery team, not the client). Comments left
  // on a decision are still shown — those are written for people.
  clientView?: boolean;
}

// ---------------------------------------------------------------------------
// Event styling
// ---------------------------------------------------------------------------

interface EventStyle {
  icon:       React.ReactNode;
  iconBg:     string;
  iconColor:  string;
  label:      string;
}

function getEventStyle(eventType: string): EventStyle {
  switch (eventType) {
    case 'workflow_started':
      return { icon: <PlayCircle size={14} />, iconBg: 'bg-sky', iconColor: 'text-blue', label: 'Workflow Started' };
    case 'workflow_completed':
      return { icon: <CheckCircle size={14} />, iconBg: 'bg-success/15', iconColor: 'text-success', label: 'Workflow Completed' };
    case 'workflow_rejected':
      return { icon: <XCircle size={14} />, iconBg: 'bg-danger/12', iconColor: 'text-danger', label: 'Workflow Rejected' };
    case 'workflow_cancelled':
      return { icon: <AlertCircle size={14} />, iconBg: 'bg-gray', iconColor: 'text-muted', label: 'Workflow Cancelled' };
    case 'approved':
      return { icon: <CheckCircle size={14} />, iconBg: 'bg-success/15', iconColor: 'text-success', label: 'Approved' };
    case 'rejected':
      return { icon: <XCircle size={14} />, iconBg: 'bg-danger/12', iconColor: 'text-danger', label: 'Rejected' };
    case 'delegated':
      return { icon: <UserCheck size={14} />, iconBg: 'bg-sky', iconColor: 'text-blue', label: 'Delegated' };
    case 'step_activated':
      return { icon: <Clock size={14} />, iconBg: 'bg-sky', iconColor: 'text-blue', label: 'Step Activated' };
    case 'step_skipped':
      return { icon: <SkipForward size={14} />, iconBg: 'bg-gray', iconColor: 'text-muted', label: 'Step Skipped' };
    default:
      return { icon: <Clock size={14} />, iconBg: 'bg-gray', iconColor: 'text-muted', label: formatEventType(eventType) };
  }
}

function formatEventType(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Single event row
// ---------------------------------------------------------------------------

function EventRow({ event, isLast, clientView = false }: { event: AuditEvent; isLast: boolean; clientView?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const style = getEventStyle(event.event_type);
  const hasPayload = !clientView && event.payload && Object.keys(event.payload).length > 0;

  return (
    <div className="flex gap-3">
      {/* Timeline line + icon */}
      <div className="flex flex-col items-center">
        <div className={['w-7 h-7 rounded-full flex items-center justify-center shrink-0 z-10', style.iconBg, style.iconColor].join(' ')}>
          {style.icon}
        </div>
        {!isLast && <div className="w-px flex-1 mt-1" style={{ background: 'rgba(8,17,31,0.08)', minHeight: '16px' }} />}
      </div>

      {/* Content */}
      <div className={['flex-1 pb-4', isLast ? '' : ''].join('')}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <span className="text-sm font-medium text-ink">{style.label}</span>
            {event.actor_name && (
              <span className="text-sm text-muted ml-1.5">
                by <span className="text-ink">{event.actor_name}</span>
              </span>
            )}
          </div>
          <span className="text-[11px] font-mono text-muted whitespace-nowrap shrink-0 mt-0.5">
            {formatTime(event.created_at)}
          </span>
        </div>

        {/* Comment from payload */}
        {event.payload?.comment != null
          ? <p className="mt-1 text-sm text-ink/70 italic">"{String(event.payload.comment)}"</p>
          : null
        }

        {/* Expandable raw payload */}
        {hasPayload && (
          <div className="mt-1.5">
            <button
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 text-[11px] font-mono text-muted hover:text-ink transition-colors"
            >
              {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
              {expanded ? 'Hide' : 'Show'} details
            </button>
            {expanded && (
              <pre className="mt-1.5 text-[11px] font-mono text-ink/70 bg-gray rounded p-2 overflow-x-auto">
                {JSON.stringify(event.payload, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AuditTrail component
// ---------------------------------------------------------------------------

export function AuditTrail({ events, loading, className = '', clientView = false }: AuditTrailProps) {
  if (loading) {
    return (
      <div className={['space-y-3 animate-pulse', className].join(' ')}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <div className="w-7 h-7 rounded-full bg-gray shrink-0" />
            <div className="flex-1 space-y-1 pt-1">
              <div className="h-3.5 bg-gray rounded w-40" />
              <div className="h-3 bg-gray rounded w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className={['text-center py-8 text-sm text-muted', className].join(' ')}>
        No audit events recorded yet.
      </div>
    );
  }

  return (
    <div className={['space-y-0', className].join(' ')}>
      {events.map((event, i) => (
        <EventRow key={event.id} event={event} isLast={i === events.length - 1} clientView={clientView} />
      ))}
    </div>
  );
}

// Re-export Badge for convenience
export { Badge };
